'use client';

/**
 * AssignmentPaymentModal — checkout de ÓRDENES de asignación (plan, 4.4).
 *
 * Flujo orden durable: client_request_id por intento → POST
 * /api/assignment-orders → orderId + depósito PERSISTIDO → hash Bold de ese
 * monto → botón embebido. El navegador solo guarda el orderId (cero
 * sessionStorage de booking). RNPL: misma orden, procesamiento directo y
 * éxito multi-reserva con TODOS los números.
 *
 * Es un modal hermano de GuestPaymentModal (el flujo simple NO se toca —
 * prohibición del plan); reutiliza los hooks compartidos de components/payments.
 */
import React, { useEffect, useRef, useState } from 'react';
import {
  X, Loader2, ShieldCheck, Lock, AlertCircle, CheckCircle2, MessageCircle, Clock,
} from 'lucide-react';
import PhoneInput from '@/components/apartments/PhoneInput';
import { BANK_PAYMENT_CONFIG, formatCurrency } from '@/lib/payment-config';
import { usePaymentForm } from '@/components/payments/usePaymentForm';
import { useBoldPayment, friendlyPaymentError } from '@/components/payments/useBoldPayment';
import { TurnstileGate } from '@/components/payments/TurnstileGate';
import { trackPostHogEvent, type CTAEventProps } from '@/lib/analytics/posthog';

export interface AssignmentSelectionLine {
  kind: 'named' | 'assignment';
  ref: string;
  units: number;
  displayName: string;
  totalPerUnit: number;
}

export interface AssignmentSelection {
  optionFingerprint: string;
  lines: AssignmentSelectionLine[];
  totalCop: number;
  depositCop: number;
  nights: number;
  arrival: string;
  departure: string;
  adults: number;
  children: number;
  hasAssignmentItems: boolean;
}

interface OrderStatusItem {
  displayName: string;
  bookingId: number | null;
  kind: 'named' | 'assignment';
}

interface AssignmentPaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  selection: AssignmentSelection | null;
  ctaAttribution: CTAEventProps;
}

type ViewMode = 'form' | 'rnpl-success';

function newUuid(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

export default function AssignmentPaymentModal({
  isOpen,
  onClose,
  selection,
  ctaAttribution,
}: AssignmentPaymentModalProps) {
  const {
    guestData, touched, errors, handleBlur, handleChange, validateAllAndScroll, resetForm,
  } = usePaymentForm('asg-field-');

  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [view, setView] = useState<ViewMode>('form');
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const [honeypot, setHoneypot] = useState('');
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [rnplLoading, setRnplLoading] = useState(false);
  const [orderItems, setOrderItems] = useState<OrderStatusItem[] | null>(null);
  const [orderStatus, setOrderStatus] = useState<string | null>(null);
  const [orderId, setOrderIdState] = useState<string | null>(null);

  // client_request_id por INTENTO de checkout (se regenera al abrir el modal;
  // los retries del mismo intento lo reúsan → doble clic inocuo)
  const clientRequestIdRef = useRef<string>(newUuid());

  const depositCop = selection?.depositCop ?? 0;
  const totalCop = selection?.totalCop ?? 0;
  const pendingBalance = totalCop - depositCop;

  const boldDescription = selection
    ? `Anticipo reserva grupo ${selection.arrival} a ${selection.departure} (${selection.lines.reduce((a, l) => a + l.units, 0)} apartamentos)`
    : '';

  const {
    integrityHash, generatingHash, setGeneratingHash, setIntegrityHash, setOrderId, resetBold,
  } = useBoldPayment({
    containerId: 'assignment-modal-bold-button-container',
    amount: depositCop,
    description: boldDescription,
    guestData,
    fallbackRedirectOrigin: 'https://pacartagena.up.railway.app',
    onError: setPaymentError,
  });

  useEffect(() => {
    if (isOpen) {
      clientRequestIdRef.current = newUuid();
    } else {
      resetForm();
      setPaymentError(null);
      resetBold();
      setAcceptedPrivacy(false);
      setView('form');
      setOrderItems(null);
      setOrderStatus(null);
      setOrderIdState(null);
      setRnplLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen || !selection) return null;

  const createOrder = async (paymentMethod: 'bold' | 'rnpl'): Promise<{ orderId: string; depositCop: number } | null> => {
    const res = await fetch('/api/assignment-orders', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        clientRequestId: clientRequestIdRef.current,
        arrival: selection.arrival,
        departure: selection.departure,
        adults: selection.adults,
        children: selection.children,
        paymentMethod,
        guestName: guestData.name,
        guestEmail: guestData.email,
        guestPhone: guestData.phone,
        selection: {
          optionFingerprint: selection.optionFingerprint,
          lines: selection.lines.map((l) => ({ kind: l.kind, ref: l.ref, units: l.units })),
        },
        _hp: honeypot,
        turnstileToken: turnstileToken || undefined,
      }),
    });
    const data = await res.json();
    if (res.status === 409) {
      setPaymentError(data.error || 'La cotización cambió. Vuelve a buscar para ver el precio vigente.');
      return null;
    }
    if (!res.ok || !data.success) {
      throw new Error(data.error || 'No pudimos crear tu orden. Intenta de nuevo.');
    }
    setOrderIdState(data.orderId);
    trackPostHogEvent('assignment_order_created', {
      ...ctaAttribution,
      order_id: data.orderId,
      payment_method: paymentMethod,
      total_cop: data.totalCop,
      deposit_cop: data.depositCop,
      units: selection.lines.reduce((a, l) => a + l.units, 0),
      people: selection.adults + selection.children,
      nights: selection.nights,
    });
    return { orderId: data.orderId, depositCop: data.depositCop };
  };

  // ── Bold: orden durable ANTES de abrir la pasarela ──────────────────────
  const handleBoldPayment = async () => {
    setPaymentError(null);
    if (!validateAllAndScroll()) return;
    setGeneratingHash(true);
    try {
      const order = await createOrder('bold');
      if (!order) {
        setGeneratingHash(false);
        return;
      }
      // Hash del monto PERSISTIDO (el server ya guardó deposit_cop)
      const hashRes = await fetch('/api/bold/generate-hash', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orderId: order.orderId, amount: order.depositCop, currency: 'COP' }),
      });
      const hashData = await hashRes.json();
      if (!hashRes.ok || !hashData.success) {
        throw new Error(hashData.error || 'Error preparando el pago');
      }
      setOrderId(order.orderId);
      setIntegrityHash(hashData.hash);
      setTimeout(() => setGeneratingHash(false), 500);
    } catch (error: unknown) {
      setPaymentError(friendlyPaymentError(error, 'Error al preparar el pago. Intenta de nuevo.'));
      setGeneratingHash(false);
    }
  };

  // ── RNPL: misma orden, procesamiento directo ─────────────────────────────
  const handleRnpl = async () => {
    setPaymentError(null);
    if (!validateAllAndScroll()) return;
    setRnplLoading(true);
    try {
      const order = await createOrder('rnpl');
      if (!order) {
        setRnplLoading(false);
        return;
      }
      // Esperar el procesamiento (after() server-side) consultando el GET
      let status = 'processing';
      let items: OrderStatusItem[] = [];
      for (let i = 0; i < 15; i++) {
        await new Promise((r) => setTimeout(r, 2000));
        const res = await fetch(`/api/assignment-orders/${order.orderId}`);
        if (!res.ok) continue;
        const data = await res.json();
        if (data.success && data.order) {
          status = data.order.status;
          items = data.order.items;
          if (['booked', 'partially_booked', 'manual_action'].includes(status)) break;
        }
      }
      setOrderStatus(status);
      setOrderItems(items);
      setView('rnpl-success');
    } catch (error: unknown) {
      setPaymentError(friendlyPaymentError(error, 'Error al crear la reserva. Intenta de nuevo.'));
    } finally {
      setRnplLoading(false);
    }
  };

  const totalUnits = selection.lines.reduce((a, l) => a + l.units, 0);
  const whatsappNumber = BANK_PAYMENT_CONFIG.whatsappSupport.replace(/[^0-9]/g, '');

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/60 sm:backdrop-blur-sm animate-in fade-in-0 duration-300"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="assignment-modal-title"
    >
      <div
        className="relative w-full sm:max-w-md md:max-w-4xl bg-white shadow-2xl flex flex-col h-[100dvh] sm:h-auto sm:max-h-[92vh] sm:rounded-2xl rounded-t-2xl animate-in slide-in-from-bottom sm:zoom-in-95 sm:slide-in-from-bottom-0 duration-300"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ━━━ HEADER ━━━ */}
        <div className="flex-shrink-0 sticky top-0 bg-white/95 backdrop-blur-md border-b border-gray-100 px-5 sm:px-6 py-4 sm:rounded-t-2xl rounded-t-2xl z-10">
          <button
            onClick={onClose}
            className="absolute right-3 top-3 rounded-full p-2 text-gray-500 hover:text-gray-800 hover:bg-gray-100 transition-colors"
            aria-label="Cerrar modal"
          >
            <X className="h-5 w-5" />
          </button>
          {view === 'form' ? (
            <>
              <h2 id="assignment-modal-title" className="text-lg font-bold text-gray-900 pr-8">
                Completa tu reserva
              </h2>
              <p className="text-xs text-gray-600 mt-0.5 truncate pr-8">
                {totalUnits} {totalUnits === 1 ? 'apartamento' : 'apartamentos'} ·{' '}
                {selection.arrival} → {selection.departure}
              </p>
            </>
          ) : (
            <h2 id="assignment-modal-title" className="text-lg font-bold text-gray-900 pr-8">
              ¡Reserva creada!
            </h2>
          )}
        </div>

        {/* ━━━ BODY ━━━ */}
        <div className="flex-1 overflow-y-auto px-5 py-5 md:px-6 md:py-6">
          {view === 'rnpl-success' ? (
            <AssignmentSuccessView
              orderId={orderId}
              status={orderStatus}
              items={orderItems}
              hasAssignmentItems={selection.hasAssignmentItems}
              totalCop={totalCop}
              guestName={guestData.name}
              whatsappNumber={whatsappNumber}
            />
          ) : (
            <div className="md:grid md:grid-cols-2 md:gap-x-10 md:gap-y-5">
              {/* ━━━ IZQUIERDA: resumen multi-línea ━━━ */}
              <div className="space-y-5">
                <div className="bg-gray-50/70 rounded-xl p-4">
                  <h3 className="text-[11px] font-medium text-gray-500 uppercase tracking-wider mb-3">
                    Resumen · {selection.nights} {selection.nights === 1 ? 'noche' : 'noches'}
                  </h3>
                  <div className="space-y-2">
                    {selection.lines.map((line, i) => (
                      <div key={i} className="flex items-center justify-between gap-2 text-sm">
                        <span className="text-gray-600 min-w-0 truncate">
                          {line.units > 1 ? `${line.units} × ` : ''}{line.displayName}
                        </span>
                        <span className="text-gray-900 font-medium tabular-nums flex-shrink-0">
                          {formatCurrency(line.totalPerUnit * line.units)}
                        </span>
                      </div>
                    ))}
                    <div className="flex items-center justify-between pt-3 mt-1 border-t border-gray-200">
                      <span className="text-sm text-gray-900">Total</span>
                      <span className="text-lg font-medium text-gray-900 tracking-tight tabular-nums">
                        {formatCurrency(totalCop)}
                      </span>
                    </div>
                    {/* El depósito es el héroe del paso de pago (auditoría Gemini
                        2026-06-12, aceptado): es lo que se cobra HOY */}
                    <div className="flex items-center justify-between rounded-lg bg-emerald-50 px-3 py-2">
                      <span className="text-sm font-medium text-emerald-900">Reservas hoy con</span>
                      <span className="text-xl font-bold text-emerald-900 tracking-tight tabular-nums">
                        {formatCurrency(depositCop)}
                      </span>
                    </div>
                    {pendingBalance > 0 && (
                      <p className="text-[11px] text-gray-500">
                        Saldo al llegar: {formatCurrency(pendingBalance)} · El anticipo equivale a una
                        noche por apartamento
                      </p>
                    )}
                  </div>
                </div>
                {selection.hasAssignmentItems && (
                  <p className="text-xs text-gray-600 leading-relaxed px-1">
                    Como en un hotel: tu categoría queda confirmada al instante y el piso y
                    número exacto de tu apartamento te los confirmamos antes del check-in.
                    Todas las unidades de este tipo están en el mismo edificio, con el mismo
                    lobby, piscina y ubicación frente al mar.
                  </p>
                )}
              </div>

              {/* ━━━ DERECHA: datos + pago ━━━ */}
              <div className="space-y-5 mt-5 md:mt-0">
                {!integrityHash && (
                  <div>
                    <h3 className="text-[11px] font-medium text-gray-500 uppercase tracking-wider mb-3">
                      Datos del titular
                    </h3>
                    <div className="space-y-3">
                      <AsgField
                        id="asg-field-name"
                        label="Nombre completo"
                        type="text"
                        autoComplete="name"
                        placeholder="Juan Pérez García"
                        value={guestData.name}
                        onChange={(v) => handleChange('name', v)}
                        onBlur={() => handleBlur('name')}
                        error={touched.name ? errors.name : undefined}
                      />
                      <PhoneInput
                        id="asg-field-phone"
                        label="Teléfono / WhatsApp"
                        value={guestData.phone}
                        onChange={(v) => handleChange('phone', v)}
                        onBlur={() => handleBlur('phone')}
                        error={touched.phone ? errors.phone : undefined}
                      />
                      <AsgField
                        id="asg-field-email"
                        label="Correo electrónico"
                        type="email"
                        autoComplete="email"
                        placeholder="juan@ejemplo.com"
                        value={guestData.email}
                        onChange={(v) => handleChange('email', v)}
                        onBlur={() => handleBlur('email')}
                        error={touched.email ? errors.email : undefined}
                      />
                    </div>
                  </div>
                )}

                {paymentError && (
                  <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-3">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-red-900 font-medium flex-1">{paymentError}</p>
                    </div>
                  </div>
                )}

                {integrityHash ? (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-3 text-center">
                      Completa tu pago seguro
                    </h3>
                    <div
                      id="assignment-modal-bold-button-container"
                      className="flex justify-center min-h-[60px] items-center"
                    >
                      {generatingHash && (
                        <div className="flex flex-col items-center gap-2">
                          <Loader2 className="h-7 w-7 animate-spin text-blue-600" />
                          <p className="text-xs text-gray-600">Cargando pasarela...</p>
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => {
                        setIntegrityHash(null);
                        setOrderId(null);
                        setPaymentError(null);
                      }}
                      className="w-full text-xs text-gray-500 hover:text-gray-900 underline mt-3 text-center"
                    >
                      ← Editar mis datos
                    </button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <h3 className="text-[11px] font-medium text-gray-500 uppercase tracking-wider">
                      Cómo pagar
                    </h3>

                    <input
                      type="text"
                      name="website"
                      value={honeypot}
                      onChange={(e) => setHoneypot(e.target.value)}
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      className="absolute opacity-0 pointer-events-none -z-10 h-0 w-0"
                    />
                    <TurnstileGate onToken={setTurnstileToken} />

                    <label className="flex items-start gap-2.5 cursor-pointer select-none py-1">
                      <input
                        type="checkbox"
                        checked={acceptedPrivacy}
                        onChange={(e) => setAcceptedPrivacy(e.target.checked)}
                        className="mt-0.5 h-4 w-4 rounded border-gray-300 text-gray-900 focus:ring-2 focus:ring-gray-900 focus:ring-offset-1 cursor-pointer"
                      />
                      <span className="text-[11px] text-gray-600 leading-relaxed">
                        Acepto la{' '}
                        <a
                          href="/privacidad"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-gray-900 underline underline-offset-2 hover:text-gray-700"
                        >
                          política de privacidad
                        </a>{' '}
                        y autorizo el tratamiento de mis datos personales
                      </span>
                    </label>

                    <button
                      onClick={handleBoldPayment}
                      disabled={generatingHash || rnplLoading || !acceptedPrivacy}
                      className="w-full bg-gray-900 text-white py-3.5 rounded-xl font-medium hover:bg-gray-800 transition-all disabled:opacity-40 disabled:cursor-not-allowed shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-gray-900 focus:ring-offset-2 flex flex-col items-center justify-center gap-0.5"
                    >
                      {generatingHash ? (
                        <span className="flex items-center gap-2 py-1">
                          <Loader2 className="h-5 w-5 animate-spin" />
                          Preparando pago...
                        </span>
                      ) : (
                        <>
                          <span>Pagar {formatCurrency(depositCop)} con tarjeta</span>
                          <span className="text-[10px] text-gray-300 font-normal">
                            Visa · Mastercard · Amex · PSE · Nequi
                          </span>
                        </>
                      )}
                    </button>

                    <button
                      onClick={handleRnpl}
                      disabled={generatingHash || rnplLoading || !acceptedPrivacy}
                      className="w-full bg-white border border-gray-200 text-gray-700 py-3 rounded-xl font-medium hover:bg-gray-50 hover:border-gray-300 transition-all disabled:opacity-40 disabled:cursor-not-allowed shadow-sm flex flex-col items-center justify-center gap-0.5"
                    >
                      {rnplLoading ? (
                        <span className="flex items-center gap-2 py-1 text-sm">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Registrando tus apartamentos...
                        </span>
                      ) : (
                        <>
                          <span className="text-sm">Reservar y coordinar pago</span>
                          <span className="text-[10px] text-gray-500 font-normal">
                            Sin pago inmediato — te contactamos por WhatsApp
                          </span>
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>

              <div className="md:col-span-2 flex items-center justify-center gap-5 pt-4 mt-5 border-t border-gray-100">
                <div className="flex items-center gap-1.5 text-[11px] text-gray-500">
                  <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />
                  <span>Pago seguro</span>
                </div>
                <div className="flex items-center gap-1.5 text-[11px] text-gray-500">
                  <Lock className="h-3.5 w-3.5 text-gray-500" />
                  <span>SSL encriptado</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// SUB-COMPONENTES
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

function AsgField({
  id, label, type, autoComplete, placeholder, value, onChange, onBlur, error,
}: {
  id: string; label: string; type: 'text' | 'email'; autoComplete: string;
  placeholder: string; value: string; onChange: (v: string) => void;
  onBlur: () => void; error?: string;
}) {
  return (
    <div>
      <label htmlFor={id} className="block text-xs text-gray-600 mb-1">{label}</label>
      <input
        id={id}
        type={type}
        name={id.replace('asg-field-', '')}
        autoComplete={autoComplete}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        placeholder={placeholder}
        aria-invalid={!!error}
        aria-describedby={error ? `${id}-error` : undefined}
        className={`w-full px-3 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-1 transition-colors ${
          error
            ? 'border-red-400 focus:ring-red-200 bg-red-50'
            : 'border-gray-200 focus:ring-gray-900/10 focus:border-gray-900'
        }`}
      />
      {error && (
        <p id={`${id}-error`} className="text-xs text-red-600 mt-1">{error}</p>
      )}
    </div>
  );
}

function AssignmentSuccessView({
  orderId, status, items, hasAssignmentItems, totalCop, guestName, whatsappNumber,
}: {
  orderId: string | null;
  status: string | null;
  items: OrderStatusItem[] | null;
  hasAssignmentItems: boolean;
  totalCop: number;
  guestName: string;
  whatsappNumber: string;
}) {
  const booked = status === 'booked' || status === 'partially_booked';
  const created = (items ?? []).filter((it) => it.bookingId);

  const message = encodeURIComponent(
    `Hola! Soy ${guestName}. Acabo de reservar un grupo de apartamentos en pacartagena.com.\n\n` +
      `📋 *Orden:* ${orderId}\n` +
      (created.length
        ? created.map((it, i) => `Reserva #${i + 1}: ${it.bookingId} — ${it.displayName}`).join('\n') + '\n'
        : '') +
      `💰 *Total:* ${formatCurrency(totalCop)}\n\nQuiero coordinar el pago.`
  );
  const whatsappLink = `https://wa.me/${whatsappNumber}?text=${message}`;

  return (
    <div className="space-y-5 max-w-md mx-auto">
      <div className="text-center">
        <div className={`inline-flex items-center justify-center w-14 h-14 rounded-full mb-3 ${booked ? 'bg-emerald-500' : 'bg-gray-200'}`}>
          {booked ? (
            <CheckCircle2 className="h-8 w-8 text-white" />
          ) : (
            <Clock className="h-8 w-8 text-gray-600" />
          )}
        </div>
        <h3 className="text-xl font-medium text-gray-900 mb-1">
          {booked ? 'Reserva confirmada' : 'Estamos registrando tus apartamentos'}
        </h3>
        <p className="text-sm text-gray-600">
          {booked
            ? `Tus ${created.length} ${created.length === 1 ? 'apartamento queda' : 'apartamentos quedan'} a tu nombre`
            : 'Tu orden quedó creada; te confirmamos los números por WhatsApp en unos minutos'}
        </p>
      </div>

      {created.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-[11px] text-gray-500 uppercase tracking-wider text-center">
            {created.length === 1 ? 'Número de reserva' : 'Números de reserva'}
          </p>
          {created.map((it, i) => (
            <div key={i} className="flex items-center justify-between gap-2 rounded-lg bg-gray-50 px-3 py-2 text-sm">
              <span className="text-gray-600 min-w-0 truncate">
                Reserva #{i + 1}: <span className="font-semibold text-gray-900">{it.bookingId}</span>
              </span>
              <span className="text-gray-500 text-xs flex-shrink-0 max-w-[45%] truncate">{it.displayName}</span>
            </div>
          ))}
        </div>
      )}

      {orderId && (
        <p className="text-center text-[11px] text-gray-500">Orden: {orderId}</p>
      )}

      {hasAssignmentItems && (
        <div className="flex items-start gap-2 px-3 py-2.5 bg-gray-50 border border-gray-100 rounded-lg">
          <Clock className="h-4 w-4 text-gray-500 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-gray-700">
            Te contactamos por WhatsApp hoy mismo; el piso y número exacto de tu apartamento te
            los confirmamos antes del check-in.
          </p>
        </div>
      )}

      {/* Claridad post-reserva (auditoría Gemini 2026-06-12, aceptado con
          ajuste propio: la reserva SÍ existe en Beds24 — lo pendiente es el
          anticipo; decirlo sin contradecir la confirmación) */}
      <p className="text-xs text-center text-gray-600">
        Tus apartamentos quedan bloqueados a tu nombre. El anticipo se coordina contigo por
        WhatsApp — sin pago todavía.
      </p>
      <a
        href={whatsappLink}
        target="_blank"
        rel="noopener noreferrer"
        className="w-full flex items-center justify-between gap-2 px-4 py-3 rounded-xl border border-gray-200 hover:border-gray-300 hover:bg-gray-50 transition-all"
      >
        <span className="text-sm text-gray-800">Coordinar el pago por WhatsApp</span>
        <MessageCircle className="h-4 w-4 text-gray-400" />
      </a>

      <p className="text-[11px] text-center text-gray-500 pt-2">
        Recibirás un correo con los detalles de tu reserva
      </p>
    </div>
  );
}
